import { BreifInfluencerV2StatusEnum } from "@entities/brief/lib/constants";
import {
  BRAND_COST_STATUS,
  IBriefInfluencerV2,
} from "@shared/lib/types/briefInfluencerV2";
import { Tag, Tooltip } from "@shared/ui";
import { ReactNode } from "react";
import styles from "./styles.module.scss";

function StatusTag({
  label,
  color,
  tooltipTitle,
}: {
  label: string;
  color: string;
  tooltipTitle?: ReactNode;
}) {
  return (
    <Tooltip title={tooltipTitle ?? label}>
      <span className={styles.statusTagWrapper}>
        <Tag color={color} className={styles.statusTag}>
          {label}
        </Tag>
      </span>
    </Tooltip>
  );
}

function StatusDescription({ text }: { text: string }) {
  return (
    <Tooltip title={text}>
      <p className={styles.descriptionText}>{text}</p>
    </Tooltip>
  );
}

export function BriefInfluencerStatus({
  briefInfluencer,
}: {
  briefInfluencer: IBriefInfluencerV2;
}) {
  const getStatusColor = () => {
    if (briefInfluencer.isCreatorDeclined) return "red";
    if (briefInfluencer.status === BreifInfluencerV2StatusEnum.Deleted)
      return "red";
    if (briefInfluencer.isBrandDeclined) return "red";
    if (briefInfluencer.isCreatorOnboardPending) return "yellow";
    if (!briefInfluencer.isPrimaryContactAdded) return "orange";
    if (!briefInfluencer.assignedPricingPocV2) return "orange";
    if (briefInfluencer.isDeliverablesPending) return "orange";
    if (briefInfluencer.influencerCost?.cost === undefined) return "orange";
    if (briefInfluencer.brandCost?.cost === undefined) return "orange";
    if (briefInfluencer?.isQueuedForPurchaseLot) return "blue";
    if (briefInfluencer.status === BreifInfluencerV2StatusEnum.Purchased)
      return "green";
    return "blue";
  };

  const getStatusLabel = () => {
    if (briefInfluencer.status === BreifInfluencerV2StatusEnum.Deleted)
      return "Deleted";
    if (briefInfluencer.isCreatorDeclined) return "Creator Declined";
    if (briefInfluencer.isBrandDeclined) return "Brand Declined";
    if (briefInfluencer.isCreatorOnboardPending) return "Onboarding Pending";
    if (!briefInfluencer.isPrimaryContactAdded) return "Contact not found";
    if (briefInfluencer.isDeliverablesPending) return "Deliverables Pending";
    if (!briefInfluencer.assignedPricingPocV2) return "Allocation Pending";

    if (briefInfluencer.influencerCost?.cost === undefined)
      return "Awaiting Commercials";
    if (briefInfluencer.brandCost?.cost === undefined)
      return "Awaiting Brand Price";
    if (briefInfluencer?.isQueuedForPurchaseLot) return "Queued for Purchase";
    if (briefInfluencer.status === BreifInfluencerV2StatusEnum.Purchased)
      return "Purchased";
    return "Offered";
  };

  const getDescriptionText = () => {
    if (briefInfluencer.status === BreifInfluencerV2StatusEnum.Purchased) {
      if (briefInfluencer.isConfirmed) {
        return <StatusDescription text="Confirmed" />;
      }
      if (briefInfluencer.isConfirmationSent) {
        return <StatusDescription text="Confirmation Sent" />;
      }
      return <StatusDescription text="Confirmation Pending" />;
    }

    return (
      <>
        {briefInfluencer.brandCostStatus ===
          BRAND_COST_STATUS["New-Quote-Recieved"] && (
          <StatusDescription text="Brand Cost Updated" />
        )}
        {briefInfluencer.brandCostStatus ===
          BRAND_COST_STATUS["Negotiations-Requested"] && (
          <StatusDescription text="Brand Cost Nego Requested" />
        )}
        {briefInfluencer.offerStatus === "negotiations-requested" && (
          <StatusDescription text="Infl Cost Nego Requested" />
        )}
        {briefInfluencer.offerStatus === "negotiations-accepted" && (
          <StatusDescription text="Inf Cost Nego Accepted" />
        )}
      </>
    );
  };

  const statusLabel = getStatusLabel();
  const declineReason = briefInfluencer.isCreatorDeclined
    ? briefInfluencer.creatorDeclinedReason
    : briefInfluencer.isBrandDeclined
      ? briefInfluencer.brandDeclinedReason
      : undefined;

  const tagTooltip = declineReason ? (
    <>
      <div>{statusLabel}</div>
      <div>{declineReason}</div>
    </>
  ) : (
    statusLabel
  );

  return (
    <div className={styles.statusContent}>
      <StatusTag
        label={statusLabel}
        color={getStatusColor()}
        tooltipTitle={tagTooltip}
      />
      {getDescriptionText()}
    </div>
  );
}
